import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Download, ReceiptText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { fmtTime } from "@/lib/slots";

export default function BillsTab() {
  const [search, setSearch] = useState("");
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selB, setSelB] = useState({});
  const [selO, setSelO] = useState({});
  const [cust, setCust] = useState({ customer_name: "", mobile: "" });
  const [discount, setDiscount] = useState(0);
  const [payment, setPayment] = useState("cash");
  const [bills, setBills] = useState([]);
  const [billDate, setBillDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [stations, setStations] = useState([]);
  const [oneStation, setOneStation] = useState("");

  const loadBills = () => api.get("/admin/bills").then((r) => setBills(r.data));
  useEffect(() => { loadBills(); api.get("/admin/stations").then((r) => setStations(r.data)); }, []);

  const oneTap = async () => {
    if (!oneStation) return toast.error("Select a station");
    setBusy(true);
    try {
      const r = await api.post("/admin/bills/from-station", { station_id: oneStation, payment_method: "cash" });
      toast.success(`Bill ${r.data.bill_no} created`);
      loadBills(); download(r.data.id, r.data.bill_no);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const doSearch = async () => {
    const b = await api.get("/admin/bookings", { params: { search } });
    setBookings(b.data.filter((x) => x.status !== "cancelled"));
    const o = await api.get("/admin/orders");
    setOrders(o.data.filter((x) => !search || (x.mobile || "").includes(search) || (x.customer_name || "").toLowerCase().includes(search.toLowerCase())));
    const first = b.data[0];
    if (first) setCust({ customer_name: first.customer_name, mobile: first.mobile });
  };

  const generate = async () => {
    const booking_ids = Object.keys(selB).filter((k) => selB[k]);
    const order_ids = Object.keys(selO).filter((k) => selO[k]);
    if (!cust.customer_name.trim() || (!booking_ids.length && !order_ids.length)) return toast.error("Add customer + at least one item");
    setBusy(true);
    try {
      const r = await api.post("/admin/bills", { ...cust, booking_ids, order_ids, discount: +discount, payment_method: payment, payment_status: "paid" });
      toast.success(`Bill ${r.data.bill_no} created`);
      setSelB({}); setSelO({}); loadBills();
      download(r.data.id, r.data.bill_no);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const download = async (id, no) => {
    try {
      const r = await api.get(`/admin/bills/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-4">
        <h3 className="font-display font-bold mb-1">One-Tap Session Bill</h3>
        <p className="text-sm text-slate-400 mb-3">Auto-combines a station's current gaming session with its unbilled food orders into one PDF.</p>
        <div className="flex gap-2">
          <select data-testid="onetap-station" value={oneStation} onChange={(e) => setOneStation(e.target.value)} className="flex-1 h-11 rounded-xl bg-purple-950/40 border border-purple-500/30 px-3 text-sm"><option value="">Select station</option>{stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
          <Button data-testid="onetap-bill-btn" disabled={busy} onClick={oneTap} className="h-11 bg-emerald-600 hover:bg-emerald-500 font-semibold"><ReceiptText size={16} /> One-Tap Bill</Button>
        </div>
      </div>
      <div className="glass rounded-2xl p-4">
        <h3 className="font-display font-bold mb-3">Create Bill</h3>
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} /><Input data-testid="bill-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customer / phone / booking ID" className="pl-9 h-11 bg-purple-950/30 border-purple-500/30" /></div>
          <Button data-testid="bill-search-btn" onClick={doSearch} className="h-11 bg-purple-600 hover:bg-purple-500">Search</Button>
        </div>

        {bookings.length > 0 && <p className="text-xs uppercase tracking-wider text-purple-400 font-mono mb-2">Gaming Bookings</p>}
        <div className="space-y-2 mb-3">
          {bookings.map((b) => (
            <label key={b.id} className="glass rounded-xl p-3 flex items-center gap-3 cursor-pointer" data-testid={`bill-booking-${b.booking_id}`}>
              <input type="checkbox" checked={!!selB[b.id]} onChange={(e) => setSelB({ ...selB, [b.id]: e.target.checked })} />
              <span className="flex-1 text-sm">{b.booking_id} · {b.station_name} · {b.setup_name} · {fmtTime(b.start_time)}–{fmtTime(b.end_time)}</span>
              <span className="text-emerald-400 font-mono">₹{b.amount}</span>
            </label>
          ))}
        </div>
        {orders.length > 0 && <p className="text-xs uppercase tracking-wider text-purple-400 font-mono mb-2">Food Orders</p>}
        <div className="space-y-2 mb-3">
          {orders.map((o) => (
            <label key={o.id} className="glass rounded-xl p-3 flex items-center gap-3 cursor-pointer" data-testid={`bill-order-${o.order_id}`}>
              <input type="checkbox" checked={!!selO[o.id]} onChange={(e) => setSelO({ ...selO, [o.id]: e.target.checked })} />
              <span className="flex-1 text-sm">{o.order_id} · {o.station_name} · {o.items.map((i) => `${i.name}×${i.qty}`).join(", ")}</span>
              <span className="text-emerald-400 font-mono">₹{o.total}</span>
            </label>
          ))}
        </div>

        <div className="grid sm:grid-cols-4 gap-2 items-end">
          <div><label className="text-xs text-purple-400 font-mono">Customer</label><Input data-testid="bill-customer" value={cust.customer_name} onChange={(e) => setCust({ ...cust, customer_name: e.target.value })} className="h-10 bg-purple-950/30 border-purple-500/30 mt-1" /></div>
          <div><label className="text-xs text-purple-400 font-mono">Mobile</label><Input value={cust.mobile} onChange={(e) => setCust({ ...cust, mobile: e.target.value })} className="h-10 bg-purple-950/30 border-purple-500/30 mt-1" /></div>
          <div><label className="text-xs text-purple-400 font-mono">Discount ₹</label><Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="h-10 bg-purple-950/30 border-purple-500/30 mt-1" /></div>
          <select value={payment} onChange={(e) => setPayment(e.target.value)} className="h-10 rounded-xl bg-purple-950/40 border border-purple-500/30 px-3 text-sm"><option value="cash">Cash</option><option value="upi">UPI</option></select>
        </div>
        <Button data-testid="generate-bill-btn" disabled={busy} onClick={generate} className="h-11 mt-3 bg-emerald-600 hover:bg-emerald-500 font-semibold">{busy ? <Loader2 className="animate-spin" /> : <><ReceiptText size={16} /> Generate Bill & Download PDF</>}</Button>
      </div>

      <div>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-display font-bold">Recent Bills</h3>
          <div className="flex items-center gap-2">
            <input type="date" data-testid="bills-date-filter" value={billDate} onChange={(e) => setBillDate(e.target.value)} className="h-9 rounded-lg bg-purple-950/30 border border-purple-500/30 px-2 text-sm text-slate-200 [color-scheme:dark]" />
            {billDate && <Button size="sm" variant="outline" data-testid="bills-date-clear" onClick={() => setBillDate("")} className="h-9 border-purple-500/30 bg-transparent text-slate-300">Clear</Button>}
          </div>
        </div>
        <div className="space-y-2">
          {bills.filter((b) => !billDate || (b.created_at || "").slice(0, 10) === billDate).map((b) => {
            const due = b.remaining || 0;
            return (
              <div key={b.id} className="glass rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`bill-${b.bill_no}`}>
                <div>
                  <p className="font-mono text-purple-300 text-sm">{b.bill_no} <span className="text-[10px] uppercase text-sky-300">{b.payment_method}</span></p>
                  <p className="text-sm">{b.customer_name}{b.station_name ? ` · ${b.station_name}` : ""} · {(b.created_at || "").slice(0, 10)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <span className="font-display font-bold text-emerald-400">₹{b.final_amount}</span>
                    {(b.advance_paid > 0 || due > 0) && <p className="text-[11px] text-slate-400">Paid ₹{b.advance_paid || 0}{due > 0 ? ` · Due ₹${due}` : ""}</p>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => download(b.id, b.bill_no)} className="h-8 border-purple-500/30 bg-transparent"><Download size={13} /></Button>
                </div>
              </div>
            );
          })}
          {bills.filter((b) => !billDate || (b.created_at || "").slice(0, 10) === billDate).length === 0 && <p className="text-slate-500 text-sm" data-testid="bills-empty">No bills for this date.</p>}
        </div>
      </div>
    </div>
  );
}
